import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from '@tanstack/react-router';
import { EmptyState } from '../components/common/EmptyState';
import { ProjectGuard } from '../components/common/ProjectGuard';
import { useProjectStore } from '../stores/project.store';
import { useProject } from '../api/queries/projects';

export function StoryArchitectListRoute() {
  const { activeProjectId } = useProjectStore();

  return (
    <ProjectGuard>
      {activeProjectId && <StoryArchitectContent projectId={activeProjectId} />}
    </ProjectGuard>
  );
}

function StoryArchitectContent({ projectId }: { projectId: string }) {
  const { data: project, isLoading, isError } = useProject(projectId);
  const navigate = useNavigate();

  const architectId = project?.architectId;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Story Architect{project ? ` \u2014 ${project.name}` : ''}
        </Typography>
      </Stack>

      {isLoading && <CircularProgress />}
      {isError && <Alert severity="error">Failed to load project.</Alert>}

      {architectId && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <AutoFixHighIcon color="primary" sx={{ fontSize: 40 }} />
              <Stack sx={{ flex: 1 }}>
                <Typography variant="h6">Architect Session</Typography>
                <Typography variant="body2" color="text.secondary">
                  Map out beats, plot points, and narrative arcs. Use AI to
                  suggest next beats or generate a full structure.
                </Typography>
              </Stack>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={() =>
                  navigate({
                    to: '/story-architect/$sessionId',
                    params: { sessionId: architectId },
                  })
                }
              >
                Open Architect
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {!isLoading && !architectId && (
        <EmptyState
          icon={<AutoFixHighIcon sx={{ fontSize: 64 }} />}
          title="No architect session available"
          description="A story architect session will be created when the project is scaffolded. Go to the project page to get started."
        />
      )}
    </Paper>
  );
}
